import React, { useState, useMemo, useEffect } from 'react'

import styled from 'styled-components'

import CircularProgess from './CircularProgess'
import DropProgress from './DropProgress'
import FormWrapper from './FormWrapper'

const CircularMultiStepForm = ({ steps, multiStepFormName, formRef }) => {
  const [step, setStep] = useState(() => {
    const saved = parseInt(
      localStorage.getItem(`hopla_careers_${multiStepFormName}_step`)
    )
    return saved || 1
  })

  const forms = useMemo(
    () => Object.fromEntries(steps.map(({ value, form }) => [value, form])),
    []
  )

  const [updatedSteps, setUpdatedSteps] = useState(() => {
    try {
      const existingSteps = localStorage.getItem(
        `hopla_careers_${multiStepFormName}_steps`
      )
      if (!existingSteps) return steps // fallback if nothing is stored

      const parsed = JSON.parse(existingSteps)
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed
      }

      // fallback if parsed is not a valid array
      return steps
    } catch (error) {
      return steps
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(`hopla_careers_${multiStepFormName}_step`, step)
      localStorage.setItem(
        `hopla_careers_${multiStepFormName}_steps`,
        JSON.stringify(updatedSteps)
      )
    } catch (error) {
      console.error('Error saving steps to localStorage:', error)
    }
  }, [updatedSteps, multiStepFormName, step])

  const totalActiveSteps = useMemo(
    () => updatedSteps?.filter((s) => s.active === true)?.length || 1,
    [updatedSteps]
  )

  const maxSteps = useMemo(() => steps.length, [steps])

  const handleSetCurrentStep = async (value) => {
    if (formRef.current) {
      const valid = await formRef.current.onNext() // call child method
      if (!valid) return
    }

    setStep(value)

    const totalNonDisabledSteps = updatedSteps?.filter(
      (s) => s.disabled === false
    )?.length

    const newSteps = updatedSteps.map((e) => ({
      ...e,
      active: e.value <= value,
      postActive: e.value <= totalNonDisabledSteps,
    }))
    setUpdatedSteps(newSteps)
  }

  const handleNextStep = async () => {
    if (formRef.current) {
      const valid = await formRef.current.onNext() // call child method
      if (!valid) return
    }

    if (step < maxSteps) {
      const nextStep = step + 1
      setStep(nextStep)

      const newSteps = updatedSteps.map((e) => {
        let newStep = { ...e }
        if (e.value === nextStep)
          newStep = { ...e, active: true, disabled: false }

        return newStep
      })

      setUpdatedSteps(newSteps)
    }
  }

  const handlePrevStep = async () => {
    if (step > 1) {
      const prevStep = step - 1
      setStep(prevStep)

      const newSteps = updatedSteps.map((e) => {
        if (e.value === step) return { ...e, active: false, postActive: true }
        return e
      })

      setUpdatedSteps(newSteps)
    }
  }

  const handleSubmit = async () => {
    if (formRef.current) {
      const valid = await formRef.current.onSubmit() // call child method
      if (!valid) return
    }

    localStorage.removeItem(`hopla_careers_${multiStepFormName}_step`)
    localStorage.removeItem(`hopla_careers_${multiStepFormName}_steps`)
  }

  return (
    <>
      <StyledSidebar>
        <div className="in">
          <CircularProgess
            step={step}
            maxSteps={maxSteps}
            totalActiveSteps={totalActiveSteps}
          />{' '}
        </div>

        {/* TODO: Display a validation message to ensure that users cannot skip steps
      if there are validation errors */}
        <div className="sidebar-content">
          {updatedSteps?.map((e, index) => (
            <DropProgress
              key={index}
              active={e.active}
              disabled={e.disabled}
              value={e.value}
              postActive={e.postActive}
              last={e.value === 1}
              text={e.text}
              onSetStep={handleSetCurrentStep}
            />
          ))}
        </div>
      </StyledSidebar>

      <StyledFormWrapper>
        {forms[step] && (
          <FormWrapper
            first={step === 1}
            last={step === updatedSteps?.length}
            form={forms[step]}
            multiStepFormName={multiStepFormName}
            onSubmit={handleSubmit}
            onNextStep={handleNextStep}
            onPrevStep={handlePrevStep}
          />
        )}
      </StyledFormWrapper>
    </>
  )
}

const StyledSidebar = styled.aside`
  width: 100%;
  max-width: 190px;
  margin-right: 4rem;
  position: sticky;
  top: 8rem;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #fff;
    top: 5rem;
  }

  @media (max-width: 768px) {
    background: #fff;
    max-width: 100vw;
    width: 100%;
    left: 0;
    right: 0;
    z-index: 3;

    .in {
      width: 100%;
      max-width: 120px;
    }
  }

  .sidebar-content {
    margin-top: 2rem;

    @media (max-width: 768px) {
      display: flex;
      align-items: flex-start;
    }
  }
`

const StyledFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

export default CircularMultiStepForm
